// Thin fetch wrapper. Attaches the bearer token + JSON content-type, parses
// JSON responses, throws a typed error on non-2xx so TanStack Query's
// error path fires for HTTP errors (not just network errors).

import { config } from '@/config';
import { refreshIfNeeded } from '@/auth/auth-service';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'DELETE' | 'PATCH';
  body?: unknown;
  signal?: AbortSignal;
  /** Default true. Set false for unauthenticated endpoints (none today). */
  authenticated?: boolean;
}

export async function apiCall<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, authenticated = true } = options;
  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (authenticated) {
    const token = await refreshIfNeeded();
    if (!token) {
      throw new ApiError('not_authenticated', 401, { error: 'not_authenticated' });
    }
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${config.apiUrl}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  // 204 No Content: nothing to parse.
  if (response.status === 204) return undefined as T;

  // Streaming endpoints (e.g. .../source for PDFs) aren't JSON — caller
  // should hit those directly via <iframe>/<img>, not through this client.
  const text = await response.text();
  const parsed = text.length > 0 ? (JSON.parse(text) as unknown) : undefined;

  if (!response.ok) {
    const errorCode =
      parsed && typeof parsed === 'object' && parsed !== null && 'error' in parsed
        ? String((parsed as { error: unknown }).error)
        : `http_${response.status}`;
    throw new ApiError(errorCode, response.status, parsed);
  }

  return parsed as T;
}
